<script setup lang="ts">
import CheckIcon from '@/assets/icons/shared/check.svg';
import { uniqueId } from 'lodash-es';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';

const model = defineModel<boolean>({ default: false });

const { id = uniqueId(IdPrefix.Control) } = defineProps<{
  id?: string;
  error?: boolean;
  disabled?: boolean;
  readonly?: boolean;
}>();
</script>

<template>
  <div class="ui-checkbox">
    <input
      :id
      v-model="model"
      class="input"
      :class="{ _error: error }"
      type="checkbox"
      :disabled="disabled"
      :readonly="readonly"
    />

    <label
      class="label"
      :for="id"
    >
      <CheckIcon class="check" />

      <span v-if="$slots.default">
        <slot />
      </span>
    </label>
  </div>
</template>

<style scoped lang="scss">
.ui-checkbox {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.input {
  display: none;

  &:checked {
    & + .label {
      .check {
        fill: var(--color-white);
        border: var(--border-gray-600);
        background-color: var(--color-gray-600);
      }
    }
  }

  &:disabled {
    & + .label {
      opacity: 0.5;
    }
  }

  &:not(:disabled) {
    & + .label {
      cursor: pointer;
    }
  }

  &._error {
    & + .label {
      color: var(--color-red-800);

      .check {
        border: var(--border-red-800);
      }
    }

    &:checked {
      & + .label {
        .check {
          background-color: var(--color-red-800);
        }
      }
    }
  }
}

.label {
  position: relative;

  padding: 8px;
  margin: -4px -8px;

  display: flex;
  align-items: center;
  gap: 8px;

  color: var(--color-gray-800);
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);

  transition:
    opacity var(--transition-fast),
    color var(--transition-fast);

  @media (hover: hover) {
    &:hover {
      opacity: 0.7;
    }
  }
}

.check {
  width: 16px;
  height: 16px;

  fill: transparent;
  border: var(--border-gray-500);
  background-color: transparent;
  border-radius: 4px;

  transition:
    border var(--transition-fast),
    background-color var(--transition-fast),
    fill var(--transition-fast);
}
</style>
